import React, { useMemo } from 'react'
import { useNewTranslation } from 'hooks'
import { useSelector, useDispatch } from 'react-redux'
import TaskSummaryHeader from './TaskSummaryHeader'
import Icon from 'components/Icon/Icon'
import { EditingState, TaskSections } from '../'
import Divider from 'components/Shared/Divider'
import { SubListButton } from '../styles'
import { selectorEditorErrors } from 'state/error'
import ErrorDotTitle from 'components/Shared/ErrorDotTitle'
import _ from 'lodash'
import { setConfirmPopup } from 'state/confirmPopup'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import { selectRegistrationDate } from 'state/registrations'
import { useSetIdentifierRoute, useCommentCount } from 'hooks/comment'

interface RegistrationProps {
    currentState: EditingState
    onCloseSideBar?: () => void
    isSmallSideBar?: boolean
    selectedTaskSection: TaskSections
    setSelectedTaskSection: (section: TaskSections) => void
    selectRegistrationPhase: () => void
    handleSetTaskSection: (e: React.MouseEvent<HTMLDivElement>, section: TaskSections, stepUuid?: string | null) => void
}

const Registration: React.FC<RegistrationProps> = ({
    currentState,
    onCloseSideBar,
    isSmallSideBar,
    selectedTaskSection,
    setSelectedTaskSection,
    selectRegistrationPhase,
    handleSetTaskSection,
}) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const errors = useSelector(selectorEditorErrors(['registration']))
    const registrationDate = useSelector(selectRegistrationDate)
    const [updateRoute] = useSetIdentifierRoute()
    const commentCount = useCommentCount()

    // Remove max_slots as it is in Section 1
    const registrationErrors = useMemo(() => {
        if (!errors) return {}
        const result = _.cloneDeep(errors['registration'])
        if (result) delete result['max_slots']

        return result
    }, [errors])

    const showSetupErrorDot = useMemo(() => {
        if (_.isEmpty(registrationErrors)) return false
        return ['title', 'description'].some(key => Object.keys(registrationErrors).includes(key))
    }, [registrationErrors])

    const showScheduleErrorDot = useMemo(() => {
        if (_.isEmpty(registrationErrors)) return false
        return ['time_window', 'approval_expiration_days'].some(key => Object.keys(registrationErrors).includes(key))
    }, [registrationErrors])

    const showRegistrationFormErrorDot = useMemo(() => {
        if (_.isEmpty(registrationErrors)) return false
        return ['blocks', 'blocks_required'].some(key => Object.keys(registrationErrors).includes(key))
    }, [registrationErrors])

    return (
        <div className="task-card">
            <TaskSummaryHeader
                className="task-button"
                dataTestId="test-registration-tab"
                title={t('taskType.registration')}
                schedule={registrationDate}
                isActive={currentState === EditingState.REGISTRATION_PHASE}
                onActive={(): void => {
                    const isCampaignEditorValueChange = localStorage.getItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
                    if (isSmallSideBar && onCloseSideBar) {
                        onCloseSideBar()
                    }
                    if (isCampaignEditorValueChange) {
                        dispatch(
                            setConfirmPopup({
                                show: true,
                                onConfirm: () => {
                                    setSelectedTaskSection(TaskSections.SETUP)
                                    selectRegistrationPhase()
                                },
                            }),
                        )
                    } else {
                        updateRoute({ taskUuid: null, section: null, stepUuid: null })
                        setSelectedTaskSection(TaskSections.SETUP)
                        selectRegistrationPhase()
                    }
                }}
                showClose={false}
                isShowErrorDot={showSetupErrorDot || showScheduleErrorDot || showRegistrationFormErrorDot}
                icon={<Icon.RegistrationPhaseIcon className="stroke-can-change" />}
                commentCount={commentCount('registration')}
            />
            {currentState === EditingState.REGISTRATION_PHASE ? (
                <div className="steps-buttons-group non-draggable-group">
                    <SubListButton
                        isActive={selectedTaskSection === TaskSections.SETUP}
                        onClick={(e: React.MouseEvent<HTMLDivElement>): void => {
                            updateRoute({ taskUuid: null, section: TaskSections.SETUP, stepUuid: null })
                            handleSetTaskSection(e, TaskSections.SETUP)
                        }}
                        data-testid="test-campaign-task-setup"
                    >
                        <ErrorDotTitle
                            title={t('campaignEditor.createTask.setUpRegistrationPageTitle')}
                            isShowDot={showSetupErrorDot}
                            commentCount={commentCount('registration', TaskSections.SETUP)}
                        />
                    </SubListButton>
                    <SubListButton
                        onClick={(e: React.MouseEvent<HTMLDivElement>): void => {
                            updateRoute({ taskUuid: null, section: TaskSections.STEPS, stepUuid: null })
                            handleSetTaskSection(e, TaskSections.STEPS)
                        }}
                        isActive={selectedTaskSection === TaskSections.STEPS}
                        data-testid="test-campaign-task-step-editor"
                    >
                        <ErrorDotTitle
                            title={t('campaignEditor.createTask.createRegistrationFormPageTitle')}
                            isShowDot={showRegistrationFormErrorDot}
                            commentCount={commentCount('registration', TaskSections.STEPS)}
                        />
                    </SubListButton>
                    <SubListButton
                        isActive={selectedTaskSection === TaskSections.SCHEDULE}
                        onClick={(e: React.MouseEvent<HTMLDivElement>): void => {
                            updateRoute({ taskUuid: null, section: TaskSections.SCHEDULE, stepUuid: null })
                            handleSetTaskSection(e, TaskSections.SCHEDULE)
                        }}
                        data-testid="test-campaign-task-schedule"
                    >
                        <ErrorDotTitle
                            title={t('campaignEditor.createTask.scheduleRegistrationPageTitle')}
                            isShowDot={showScheduleErrorDot}
                            commentCount={commentCount('registration', TaskSections.SCHEDULE)}
                        />
                    </SubListButton>
                </div>
            ) : null}
            <Divider />
        </div>
    )
}

export default Registration
